import React, { FC, useMemo } from 'react';
import { UserCoursesHeaderContainer } from 'src/user/courses/components/UserCoursesHeader/UserCoursesHeaderContainer';
import { UserCoursesHeaderTitle } from 'src/user/courses/components/UserCoursesHeader/UserCoursesHeaderTitle';
import {
  UserCoursesSummaryItem,
  UserCoursesSummaryItemType,
} from 'src/user/courses/components/UserCoursesSummaryItem';
import { UserProfileUserInfo } from 'src/user/common/components/UserProfileUserInfo';
import styled, { css } from 'styled-components';
import { ScrollContainer } from 'src/common/components/ScrollContainer';
import { media } from 'src/config/breakpoints';
import { colors, shadows } from 'src/config/colors';
import { layers } from 'src/config/layers';
import { typography } from 'src/config/typography';
import BookIcon from 'src/assets/icons/BookIcon';
import TimerIcon from 'src/assets/icons/TimerIcon';
import CalendarIcon from 'src/assets/icons/CalendarIcon';
import CertificateIcon from 'src/assets/icons/CertificateIcon';
import { useUserSummaryQuery } from 'src/common/hooks/useUserSummaryQuery';
import { Loader } from 'src/common/components/Loader';
import { isSummaryEmpty } from 'src/common/models/user';
import { UserCoursesHeaderBlank } from 'src/user/courses/components/UserCoursesHeader/UserCoursesHeaderBlank';

const StyledScrollContainer = styled(ScrollContainer)`
  margin: -1.25rem -0.75rem 0;
  width: auto;

  ${media.w.min.px768(css`
    margin: -0.625rem -1.125rem 0;
  `)};

  ${media.w.min.px992(css`
    margin: -0.625rem -1.5rem 0;
  `)};
`;

const SummaryItems = styled.div`
  align-items: center;
  background: ${colors.white};
  border-radius: 0.375rem;
  box-shadow: ${shadows.drop1};
  display: flex;
  flex: 3 0 0;
  justify-content: space-around;
  margin-left: 0.75rem;
  padding: 2.9375rem 0;
  z-index: ${layers.contentTop};

  ${media.w.min.px768(css`
    margin-left: 1.125rem;
  `)};

  ${media.w.min.px992(css`
    margin-left: 1.5rem;
    z-index: ${layers.contentBottom};
  `)};

  ${media.w.min.px992(css`
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  `)};
`;

const SummaryLastItem = styled.div`
  display: flex;
  flex: 1 0 0;
  margin: 0.8125rem 0 0.8125rem -1.25rem;
  padding-right: 0.75rem;
  z-index: ${layers.contentBottom};

  ${media.w.min.px768(css`
    margin: 0 0 0 -2.5rem;
    padding-right: 1.125rem;
  `)};

  ${media.w.min.px992(css`
    margin-left: -2.125rem;
    padding-right: 1.5rem;
  `)};
`;

const SummaryLastItemContent = styled(SummaryItems)`
  padding-left: 2.8125rem;
  padding-right: 1.5rem;

  ${media.w.min.px992(css`
    padding-left: 3.0625rem;
    padding-right: 2.875rem;
  `)};
`;

const SummaryWrapper = styled.div`
  display: flex;
  margin: 1.875rem 0;
  width: 100%;
`;

const Text = styled.div`
  color: ${colors.red};
  font-family: ${typography.fontFamilyHeaders};
  margin-top: 1.25rem;
  position: relative;
  z-index: ${layers.contentBottom};

  ${media.w.min.px768(css`
    font-size: 1.5rem;
    line-height: 1.875rem;
    margin-top: 2.5rem;
  `)};

  ${media.w.min.px992(css`
    margin-top: 1.25rem;
  `)};
`;

interface SummaryData {
  items: UserCoursesSummaryItemType[];
  lastItem: UserCoursesSummaryItemType[];
}

export const UserCoursesHeader: FC = () => {
  const { data, isFetching, isLoading } = useUserSummaryQuery();

  const summary = useMemo<SummaryData | null>(() => {
    if (!data || isSummaryEmpty(data)) {
      return null;
    }

    return {
      items: [
        {
          icon: BookIcon,
          title: 'ukończonych<br />szkoleń',
          value: data.completedTrainings,
        },
        {
          icon: TimerIcon,
          title: 'godziny<br />uczestnictwa',
          value: data.hours,
        },
        {
          icon: CalendarIcon,
          title: `${
            data.nextEvent?.type === 'hours' ? 'godziny' : 'dni'
          }, do kolejnego<br />szkolenia`,
          value: data.nextEvent?.value,
        },
      ],
      lastItem: [
        {
          icon: CertificateIcon,
          title: 'nadanych<br />certyfikatów',
          value: data.certificates,
        },
      ],
    };
  }, [data]);

  return (
    <UserCoursesHeaderContainer>
      <UserCoursesHeaderTitle>Mój rozwój, moje szkolenia</UserCoursesHeaderTitle>

      {!isLoading && summary && <Text>Ucz się z&nbsp;nami i wspinaj się po drabinie sukcesu!</Text>}

      {isFetching && <Loader />}

      {!isFetching && !summary && <UserCoursesHeaderBlank />}

      {!isFetching && summary && (
        <StyledScrollContainer>
          <SummaryWrapper>
            <SummaryItems>
              {summary.items.map((item, index) => (
                <UserCoursesSummaryItem item={item} key={index} />
              ))}
            </SummaryItems>

            <SummaryLastItem>
              <SummaryLastItemContent>
                {summary.lastItem.map((item, index) => (
                  <UserCoursesSummaryItem item={item} key={index} />
                ))}
              </SummaryLastItemContent>
            </SummaryLastItem>
          </SummaryWrapper>
        </StyledScrollContainer>
      )}

      <UserProfileUserInfo />
    </UserCoursesHeaderContainer>
  );
};
